import { useMemo } from 'react'
import Autoplay from 'embla-carousel-autoplay'
import { WidgetProps } from '../../contracts/widget'
import { WidgetProvider, useWidget } from '../../providers/widget'
import {
  relatedPropertiesWidgetConfig,
  RelatedPropertiesWidgetConfig,
} from './config'
import { useFetchRelatedProperties } from './data'
import PropertyCard from '../../common/property-card/TemplateExplorer'
// #902 bullet 5: the same builder the explorer uses, pointed at this widget's
// own container. Type-only dependency on the explorer's config, so nothing from
// that widget's module graph reaches this bundle.
import { buildCardSkinCss, getCardSkinTitleGoogleFont } from '../../common/property-card/card-skin-css'
import type { ExploreWidgetConfig } from '../explorer/config'
import type { ExploreProperty } from '../explorer/data'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  Dots,
} from '../../components/ui/carousel'
// The shared card's CSS (.hfse-property-* / .hk-* families) — just the card
// styles, not the whole explorer stylesheet this used to pull in (which baked
// ~3000 lines of search-bar/map/date-picker CSS into this bundle).
import '../../common/property-card/explorer-card.css'
import './related-properties.css'
import Stylesheet from '../../components/Stylesheet'
import { useSlideImpressions } from './impressions'
import WebFont from '../../components/WebFont'
import WidgetSection from '../../components/WidgetSection'
import WidgetDataError from '../../components/WidgetDataError'
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"

export default function RelatedPropertiesWidget({
  options,
  feedId,
  widgetId,
  resolvedTheme,
  data,
}: WidgetProps<RelatedPropertiesWidgetConfig, Record<string, any>>) {
  const parsed = useMemo(() => parseWidgetConfig(relatedPropertiesWidgetConfig, options), [options])

  return (
    <WidgetProvider
      options={parsed}
      feedId={feedId}
      widgetId={widgetId}
      resolvedTheme={resolvedTheme}
      widgetType="related-properties"
      data={data ?? {}}
    >
      <RelatedContent />
    </WidgetProvider>
  )
}

function RelatedContent() {
  const { options, feedId, resolvedTheme } = useWidget<RelatedPropertiesWidgetConfig>()
  const { data, isLoading, isError, error } = useFetchRelatedProperties(feedId, options.filter)

  // PropertyCard expects an `ExploreWidgetConfig`. Our schema is intentionally a
  // structural subset (filter shape, color shape, priceDisplay shape) so the cast
  // is safe. Card-only paths (price + colors + meta) are exercised; explorer-only
  // fields (map, fullPrice, infinite scroll, etc.) are never read by PropertyCard.
  // The explorer's "No dates available" swap (#1064 Wave 4) is scoped to the
  // explorer; this widget's own label setting and opt-out are tracked in #1188.
  const cardOptions = useMemo<ExploreWidgetConfig>(() => {
    const base = options as unknown as ExploreWidgetConfig
    return { ...base, priceDisplay: { ...base.priceDisplay, showNoDatesLabel: false } } as ExploreWidgetConfig
  }, [options])
  const cssString = useRelatedCss()

  const containerStyle: React.CSSProperties = resolveWidgetSpacingStyle(options)

  const properties = data?.result ?? []
  const hasResults = properties.length > 0
  const showEmpty =
    !isLoading && !isError && !hasResults && Boolean(options.noItemsText)

  // Hide the entire section (including the title) when we know there are no
  // results — keeps the title visible during loading so it doesn't flash in.
  //
  // A hidden sentinel, NOT `null` (#1610). Rendering nothing leaves the SSR
  // container childless, and `hasRenderedChildren` — the one boolean that
  // decides hydrate-vs-client-render for every widget on every page — reads
  // that as "the server never rendered this". The mount then took the CSR
  // branch and started the #941 anti-CLS reservation, whose release signal is
  // "an element child reached the render target". This widget renders nothing
  // client-side either, so that signal never fired and a 440px shimmer (the
  // measured hint) sat at the bottom of every PDP with no matches until
  // MAX_HOLD_MS expired. An element child instead means the widget hydrates,
  // so no reservation is ever started and the redundant client re-resolve
  // goes away with it.
  //
  // `hidden` and empty: no box, no paint, nothing for a screen reader. This is
  // the contract mount-ssr-detection.test.ts already documents — "an empty div
  // is still a render: a widget may legitimately render nothing visible (an
  // empty result set) and must still be hydrated, not re-fetched".
  if (!isLoading && !isError && !hasResults && !showEmpty) {
    return <div hidden data-hr-empty="related-properties" />
  }

  // Show the section title only when we actually have content. Never during
  // loading (no flash) and never on the empty-with-noItemsText branch.
  const showSection = hasResults && Boolean(options.section?.show)

  return (
    <div
      className={`hfse hr-related ${resolvedTheme === 'dark' ? 'dark' : ''}`}
      style={containerStyle}
      suppressHydrationWarning
    >
      <WebFont extraFamily={getCardSkinTitleGoogleFont(options.card.titleFontFamily)} />
      <Stylesheet css={cssString} />

      <WidgetSection show={showSection} title={options.section?.title} forceHeadingInMarkup={hasResults} />

      {isLoading ? <RelatedLoading /> : null}

      {isError ? <WidgetDataError error={error} /> : null}

      {!isLoading && properties.length > 0 ? (
        options.template === 'slider' ? (
          <RelatedSlider properties={properties} cardOptions={cardOptions} />
        ) : (
          <div className="hr-related-grid">
            {properties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                options={cardOptions}
                quoteData={null}
                quoteLoading={false}
                quoteFailed={false}
                showFavoriteButton={false}
              />
            ))}
          </div>
        )
      ) : null}

      {showEmpty ? <div className="hr-related-empty">{options.noItemsText}</div> : null}
    </div>
  )
}

/**
 * Embla-based slider (shared ui/carousel — same mechanics as reviews).
 * Wires the `slider.*` settings: hideArrows, hideDots, autoplay,
 * autoplayInterval, loop. Item geometry (85%/45%/31% bases at the 600/960
 * breakpoints) lives in related-properties.css on `.hr-related-slider-item`.
 */
function RelatedSlider({
  properties,
  cardOptions,
}: {
  properties: ExploreProperty[]
  cardOptions: ExploreWidgetConfig
}) {
  const { options, feedId, widgetId } = useWidget<RelatedPropertiesWidgetConfig>()
  const slider = options.slider

  // #717 phase 1: per-card impression events (CustomEvent + dataLayer), no
  // network. Slider only — grid impressions would need an IntersectionObserver
  // analog and are out of scope there.
  const { setApi, markerRef } = useSlideImpressions(
    { widgetType: 'related-properties', template: 'slider', widgetId, feedId },
    properties,
  )

  const plugins = useMemo(
    // Autoplay's default stopOnLastSnap:false wraps to the first snap even
    // when the carousel itself is not looping — honor loop:false there too.
    () =>
      slider.autoplay
        ? [Autoplay({ delay: slider.autoplayInterval, stopOnLastSnap: !slider.loop })]
        : [],
    [slider.autoplay, slider.autoplayInterval, slider.loop],
  )

  return (
    <div className="hr-related-slider relative" ref={markerRef}>
      <Carousel
        className="hr-related-carousel"
        opts={{ loop: slider.loop, align: 'start' }}
        plugins={plugins}
        setApi={setApi}
      >
        {!slider.hideArrows && (
          <CarouselPrevious className="hr-related-carousel-prev absolute start-0 top-1/2 -translate-y-1/2 z-10 bg-muted-foreground/70 hover:bg-muted-foreground/90 text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-none" />
        )}
        <CarouselContent className="hr-related-carousel-content">
          {properties.map((property) => (
            <CarouselItem className="hr-related-slider-item" key={property.id}>
              <PropertyCard
                property={property}
                options={cardOptions}
                quoteData={null}
                quoteLoading={false}
                quoteFailed={false}
                showFavoriteButton={false}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {!slider.hideArrows && (
          <CarouselNext className="hr-related-carousel-next absolute end-0 top-1/2 -translate-y-1/2 z-10 bg-muted-foreground/70 hover:bg-muted-foreground/90 text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-none" />
        )}
        {!slider.hideDots && (
          <div className="hr-related-carousel-dots">
            <Dots />
          </div>
        )}
      </Carousel>
    </div>
  )
}

function RelatedLoading() {
  return (
    <div className="hr-related-grid">
      <div className="hr-related-skeleton" />
      <div className="hr-related-skeleton" />
      <div className="hr-related-skeleton" />
    </div>
  )
}

/**
 * Dark `--hfse-*` overrides for the auto FIRST-PAINT path (#900, mirrors
 * useExplorerCss / PropertyCardWidgetContent). The base cssString below always
 * emits the resolved (LIGHT for a light/SSR host) palette on `.hr-related`; this
 * appends the DARK set under two selector prefixes so light output stays
 * byte-identical:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark and
 *     post-hydration auto).
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * Values come from `options.darkModeColors` directly (not gated on
 * resolvedTheme) so they ship on every render. `:host(...)` (not a descendant
 * `[data-hr-theme]`) is required to reach the shadow host from inside its own
 * stylesheet; BOTH auto conditions live INSIDE `:host(...)` — a `:not` placed
 * after the parens silently never matches the host.
 */
function relatedDarkOverrides(
  prefix: string,
  dc: RelatedPropertiesWidgetConfig['darkModeColors'],
): string {
  return `
      ${prefix} .hr-related {
        --hfse-foreground: ${dc.foreground};
        --hfse-muted-foreground: ${dc.mutedForeground};
        --hfse-card-bg: ${dc.cardBackground};
        --hfse-card-fg: ${dc.cardForeground};
        --hfse-border: ${dc.borderColor};
      }
  `
}

/**
 * Inline CSS that wires the user-configurable grid columns / gap into CSS variables.
 * Static rules live in related-properties.css; only the dynamic bits are injected here.
 */
function useRelatedCss() {
  const { options, resolvedTheme } = useWidget<RelatedPropertiesWidgetConfig>()
  // Card skin (#902 bullet 5), scoped to this widget's own root. Neutral
  // settings emit '' — an untouched widget renders byte-for-byte what it did
  // before the group existed. Emitted BEFORE customCss, in the order
  // useExplorerCss uses (:418-419): parity with the explorer is the point, so
  // the two surfaces must cascade alike. Order is not what decides the winner —
  // every skin rule carries the `.hr-related` prefix and outranks the bare
  // `.hfse-property-*` selectors the Custom CSS editor inserts (title-font rules
  // add !important on top), so an operator override has to match the skin's
  // specificity, exactly as on the explorer. Only bites once a knob is set.
  // Own memo: the skin depends on `card` alone, not on theme flips or a live
  // customCss edit. Prose stays OUT of the template below — anything inside it
  // ships in every <style> the widget renders.
  const skinCss = useMemo(() => buildCardSkinCss(options.card, '.hr-related'), [options.card])
  return useMemo(() => {
    const g = options.grid
    // Only the CSS variables PropertyCard's subtree actually consumes are
    // wired (card bg/fg, foreground, muted foreground, border). The remaining
    // color keys in the schema are hidden from the form until W4.1's unified
    // card re-decides the color mapping.
    const colors =
      resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
    return `
      /* common.css paints every host with --color-background and excludes only
         layout SHELLS ([data-hr-widget-category="layout"]). This leaf binds into
         the PDP layout's bottom slot on its own unmarked host, so it has to
         cancel the paint itself or it draws an opaque band across the page. */
      :host {
        background-color: transparent !important;
      }
      .hr-related {
        --hfse-foreground: ${colors.foreground};
        --hfse-muted-foreground: ${colors.mutedForeground};
        --hfse-card-bg: ${colors.cardBackground};
        --hfse-card-fg: ${colors.cardForeground};
        --hfse-border: ${colors.borderColor};
      }
      .hr-related .hr-related-grid {
        --hr-related-cols: ${g.columns};
        --hr-related-cols-780: ${g.columns780};
        --hr-related-cols-960: ${g.columns960};
        --hr-related-gap: ${g.gap}px;
      }
      .hr-related .hr-related-slider {
        --hr-related-gap: ${g.gap}px;
      }
      ${relatedDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
      @media (prefers-color-scheme: dark) {
        ${relatedDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
      }
      ${skinCss}
      ${options.customCss ?? ''}
    `
  }, [options.grid, options.lightModeColors, options.darkModeColors, resolvedTheme, skinCss, options.customCss])
}
